import React from 'react'

import { classes } from '../utils'
import { TabProps } from './Tab'

type Props = {
  /** Child tabs */
  children: React.ReactElement<TabProps>[]
  /** ID of the selected tab. */
  selected: string
  /** On change callback to change the selected tab. */
  onChange?: (selectedTabId: string) => void
  /** Optional flag for disabling the tabs */
  disabled?: boolean
  /** Optional class name for the tab button */
  buttonClassName?: string
  /** Optional orientation of the tabs */
  orientation?: 'horizontal' | 'vertical'
}

/**
 * Tabs component.
 *
 * The component is used alongside the Tab component, as shown in the
 * following example:
 *
 * <Tabs>
 *    <Tab id="tab1" title="First tab">TODO</Tab>
 *    <Tab id="tab2" title="Second tab">TODO</Tab>
 * </Tabs>
 */
//
const Tabs = ({
  children,
  selected,
  onChange,
  disabled,
  buttonClassName,
  orientation = 'horizontal',
}: Props) => {
  return (
    <>
      <ul
        className={classes(
          'border-solid border-border-normal',
          orientation === 'horizontal'
            ? '-mb-px flex border-b'
            : '-ml-px flex-col border-l',
        )}
        aria-label="Tabs"
      >
        {React.Children.map(children, (child: React.ReactElement<TabProps>) => (
          <li>
            <button
              className={classes(
                'inline-flex h-12 items-center whitespace-nowrap border-transparent px-4 text-base outline-none focus-visible:text-foreground-normal focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-focus-outline',
                disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                {
                  ['border-border-strong text-foreground-normal']:
                    selected === child.props.id,
                },
                {
                  ['text-foreground-subtle']: selected !== child.props.id,
                },
                {
                  'hover:text-foreground-normal':
                    selected !== child.props.id && !disabled,
                },
                {
                  'border-b-2': orientation === 'horizontal',
                },
                {
                  'border-l-2': orientation === 'vertical',
                },
                buttonClassName,
              )}
              key={`tab-title-${child.props.id}`}
              data-testid={`tab-title-${child.props.id}`}
              onClick={(e) => {
                if (e) {
                  e.preventDefault()
                }
                if (onChange && !disabled) {
                  onChange(child.props.id)
                }
              }}
              onMouseEnter={child.props.onMouseEnter}
            >
              {child.props.title}
            </button>
          </li>
        ))}
      </ul>
      {React.Children.toArray(children).find((child: unknown) => {
        const elementChild = child as React.ReactElement<TabProps>
        return elementChild.props.id === selected
      })}
    </>
  )
}

Tabs.defaultProps = {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: () => {},
}

export default Tabs
